import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { ActionsSubject, Store } from '@ngrx/store';
import * as fromHeader from '~app/modules/generic/store';
import { ScrollStrategies } from '~shared/modules/popover/models/popover-scroll-strategy.enum';
import { map, take, takeUntil } from 'rxjs/operators';
import { MmPopoverCloseEvent } from '~shared/modules/popover/models/popover-event.interface';
import { ActivatedRoute } from '@angular/router';
import get from 'lodash.get';
import { MmPopoverService } from '~shared/modules/popover/services/popover.service';
import { ofType } from '@ngrx/effects';
import { CategorySelect, SearchActionTypes, StartSearch } from '~app/modules/generic/store/search';
import { HideSuggestionsList } from '~app/modules/generic/store/search-suggestions';
import { MobileMenuComponent } from '~app/modules/generic/components/header/mobile-menu/mobile-menu.component';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';

@Injectable({
  providedIn: 'root',
})
export class MobileMenuService implements OnDestroy {
  destroySubject = new Subject<boolean>();
  isMenuOpen$ = new BehaviorSubject<boolean>(false);

  private mobileMenuRef: any;
  private positionChanged$: Subject<{ [key: string]: string | number }>;
  private closeEmitter: EventEmitter<unknown>;

  constructor(
    private readonly store: Store<fromHeader.BuyerState>,
    private readonly route: ActivatedRoute,
    private readonly popoverSvc: MmPopoverService,
    private readonly actions$: ActionsSubject,
    private readonly activeFeaturesConfigCatService: ActiveFeaturesConfigcatService
  ) {
    this.actions$
      .pipe(ofType(SearchActionTypes.START_SEARCH, SearchActionTypes.CATEGORY_SELECT), takeUntil(this.destroySubject))
      .subscribe(() => this.close());
  }

  ngOnDestroy() {
    this.destroySubject.next();
  }

  show(origin) {
    this.isMenuOpen$.next(true);
    if (this.mobileMenuRef) {
      return;
    }

    this.positionChanged$ = new Subject();
    this.closeEmitter = new EventEmitter();
    this.mobileMenuRef = this.popoverSvc.open({
      origin,
      content: MobileMenuComponent,
      width: '100%',
      height: '100%',
      scrollStrategy: ScrollStrategies.block,
      hasBackdrop: false,
      data: {
        positionChanged$: this.positionChanged$,
        closeEmitter: this.closeEmitter,
      },
    });

    this.mobileMenuRef.positionChanged
      .pipe(
        map(() => this.mobileMenuRef.getOverlayClientRect()),
        takeUntil(this.destroySubject)
      )
      .subscribe(({ width, height }) => {
        this.positionChanged$.next({ width, height });
      });

    this.mobileMenuRef.afterClosed$.pipe(take(1)).subscribe((closeEvent: MmPopoverCloseEvent) => {
      const { data } = closeEvent;
      const category = get(data, 'category');
      const suggestion = get(data, 'suggestion');

      if (category) {
        this.store.dispatch(new CategorySelect(category));
      }

      if (suggestion) {
        this.store.dispatch(new StartSearch(suggestion));
      }

      this.store.dispatch(new HideSuggestionsList());
    });

    this.closeEmitter.subscribe(this.close.bind(this));
  }

  close(): void {
    if (this.mobileMenuRef) {
      this.mobileMenuRef.close();
      this.mobileMenuRef = null;
      this.isMenuOpen$.next(false);
    }
  }
}
